import { Fragment, useMemo, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type SortingState,
  type Row,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { DataGridPagination } from './DataGridPagination';

/**
 * Server-side data grid.
 *
 * TanStack Table is headless, which is the whole reason it was chosen: the DOM
 * below is ours, so it emits exactly the markup `dataTables.bootstrap5` and
 * `stla_datatables.css` already style. Swapping DataTables for a component that
 * renders its own markup would have meant restyling every grid in the approved
 * design.
 *
 * Paging, sorting and filtering all happen on the server — the row set here is
 * one page, and `total` comes from the same query. `manual*` flags tell the
 * table not to try doing any of it locally.
 */

/**
 * Per-column presentation the grid applies to both the header and the cells.
 *
 * This is TanStack's `meta` escape hatch, declared once here so every grid spells
 * it the same way. `align` replaces what the Razor grids did with DataTables'
 * `columnDefs: [{ targets: [...], className: 'text-center' }]` — the classes are
 * the same Bootstrap utilities, they are just declared on the column instead of
 * by column index.
 */
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'start' | 'center' | 'end';
  }
}

const ALIGN_CLASS = {
  start: 'text-start',
  center: 'text-center',
  end: 'text-end',
} as const;

export interface DataGridProps<TRow> {
  columns: ColumnDef<TRow, unknown>[];
  rows: TRow[];
  /** Total matching rows on the server, for the pager. */
  total: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  isLoading?: boolean;
  isFetching?: boolean;
  /** Stable row identity; falls back to the row index. */
  getRowId?: (row: TRow, index: number) => string;
  /** Renders an expanded detail row beneath a row, e.g. the Takata call log. */
  renderSubRow?: (row: Row<TRow>) => ReactNode;
  /**
   * Which rows are expanded. Owned by the caller so the detail content can be
   * fetched and cached per row — the Takata call log is a separate request.
   * Without it `renderSubRow` never renders, since no row reports as expanded.
   */
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  /** Extra classes on the <table>, on top of the design's defaults. */
  tableClassName?: string;
  /** Shown in place of rows when the page is empty. */
  emptyMessage?: ReactNode;
  /** Row of per-column filter controls, rendered in a second header row. */
  filterRow?: ReactNode;
  pageSizeOptions?: number[];
}

export function DataGrid<TRow>({
  columns,
  rows,
  total,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  isLoading = false,
  isFetching = false,
  getRowId,
  renderSubRow,
  expanded,
  onExpandedChange,
  tableClassName = '',
  emptyMessage,
  filterRow,
  pageSizeOptions,
}: DataGridProps<TRow>) {
  const { t } = useTranslation();

  const pageCount = useMemo(
    () => (pagination.pageSize > 0 ? Math.ceil(total / pagination.pageSize) : 0),
    [total, pagination.pageSize],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { pagination, sorting, expanded: expanded ?? {} },
    onPaginationChange,
    onSortingChange,
    onExpandedChange,
    getCoreRowModel: getCoreRowModel(),
    // Only wired when the caller asked for detail rows; every row can expand,
    // because whether there is anything to show is only known after fetching it.
    getExpandedRowModel: renderSubRow ? getExpandedRowModel() : undefined,
    getRowCanExpand: renderSubRow ? () => true : undefined,
    getRowId,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount,
  });

  const columnCount = table.getVisibleFlatColumns().length;

  return (
    <div className="dataTables_wrapper dt-bootstrap5">
      <div className="table-responsive">
        <table
          className={`table table-bordered table-hover table-striped w-100 dataTable ${tableClassName}`.trim()}
        >
          <thead className="thead-themed">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const direction = header.column.getIsSorted();

                  // These are the class names DataTables puts on its own
                  // headers, so the sort arrows in the stylesheet keep working.
                  const sortClass = !canSort
                    ? ''
                    : direction === 'asc'
                      ? 'sorting sorting_asc'
                      : direction === 'desc'
                        ? 'sorting sorting_desc'
                        : 'sorting';

                  const align = header.column.columnDef.meta?.align;

                  return (
                    <th
                      key={header.id}
                      className={`${sortClass} ${align ? ALIGN_CLASS[align] : ''}`.trim()}
                      style={{ width: header.getSize() ? `${header.getSize()}px` : undefined }}
                      aria-sort={
                        direction === 'asc'
                          ? 'ascending'
                          : direction === 'desc'
                            ? 'descending'
                            : undefined
                      }
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      role={canSort ? 'button' : undefined}
                      tabIndex={canSort ? 0 : undefined}
                      onKeyDown={
                        canSort
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                header.column.toggleSorting();
                              }
                            }
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  );
                })}
              </tr>
            ))}
            {filterRow}
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columnCount} className="text-center py-4">
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  {t('Loading', 'Loading...')}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className="dataTables_empty text-center py-4">
                  {emptyMessage ?? t('NoRecordsFound', 'No matching records found')}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr className={row.getIsSelected() ? 'selected' : undefined}>
                    {row.getVisibleCells().map((cell) => {
                      const align = cell.column.columnDef.meta?.align;
                      return (
                        <td key={cell.id} className={align ? ALIGN_CLASS[align] : undefined}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                  {renderSubRow && row.getIsExpanded() ? (
                    <tr className="sub-row">
                      <td colSpan={columnCount} className="p-0">
                        {renderSubRow(row)}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <DataGridPagination
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        total={total}
        pageCount={pageCount}
        isFetching={isFetching}
        pageSizeOptions={pageSizeOptions}
      />
    </div>
  );
}
